/**
 * OAuth2 Delegation Helper
 *
 * Provides utilities for the frontend to request delegation tokens
 * when the user interacts with the chatbot. The delegation token is
 * then passed to the chatbot so it can call the API on behalf of the user.
 *
 * Flow:
 *   1. User is logged in (has access token via AuthContext)
 *   2. User opens / interacts with the chatbot widget
 *   3. Frontend calls `requestDelegation(clientId)` → delegation token
 *   4. Frontend passes delegation token to the chatbot (e.g. via postMessage or WebSocket)
 *   5. Chatbot uses the delegation token as its Authorization header
 */

import { api, getAccessToken } from './api';
import type { OAuthDelegationResponse, OAuthScope } from '@runninglime/shared';

// Cache active delegation to avoid re-requesting on every message
let cachedDelegation: {
  token: string;
  expiresAt: number; // epoch ms
  clientId: string;
} | null = null;

const EXPIRY_BUFFER_MS = 60_000; // refresh 1 min before expiry

/**
 * Request a delegation token for the given chatbot client.
 * Results are cached until near expiry.
 *
 * @param clientId - The OAuth2 client ID of the chatbot
 * @param scopes  - Optional scope subset (defaults to all client scopes)
 * @returns The delegation token string, or null if the user is not authenticated
 */
export async function requestDelegation(
  clientId: string,
  scopes?: OAuthScope[]
): Promise<string | null> {
  // Check cache
  if (
    cachedDelegation &&
    cachedDelegation.clientId === clientId &&
    cachedDelegation.expiresAt > Date.now() + EXPIRY_BUFFER_MS
  ) {
    return cachedDelegation.token;
  }

  // Must have a user access token
  if (!getAccessToken()) {
    return null;
  }

  const res = await api<OAuthDelegationResponse>('/oauth/delegate', {
    method: 'POST',
    body: { client_id: clientId, ...(scopes ? { scopes } : {}) },
  });

  if (!res.success || !res.data) {
    console.warn('[delegation] Failed to obtain delegation token:', res.error);
    cachedDelegation = null;
    return null;
  }

  cachedDelegation = {
    token: res.data.delegationToken,
    expiresAt: Date.now() + res.data.expiresIn * 1000,
    clientId,
  };

  return cachedDelegation.token;
}

/**
 * Revoke the current delegation (e.g. when user closes the chatbot).
 */
export async function revokeDelegation(): Promise<void> {
  if (!cachedDelegation) return;

  // We need to extract the delegation ID from the token to revoke it.
  // For simplicity, we call the revoke endpoint which accepts delegation_id.
  // The delegation ID is embedded in the JWT — decode it client-side.
  try {
    const payload = JSON.parse(atob(cachedDelegation.token.split('.')[1]));
    if (payload.delegationId) {
      await api('/oauth/revoke', {
        method: 'POST',
        body: { delegation_id: payload.delegationId },
      });
    }
  } catch {
    // Best-effort revocation
  }

  cachedDelegation = null;
}

/**
 * Clear cached delegation (call on user logout).
 */
export function clearDelegationCache(): void {
  cachedDelegation = null;
}

/**
 * Check whether we have an active (non-expired) delegation.
 */
export function hasDelegation(clientId?: string): boolean {
  if (!cachedDelegation) return false;
  if (clientId && cachedDelegation.clientId !== clientId) return false;
  return cachedDelegation.expiresAt > Date.now() + EXPIRY_BUFFER_MS;
}
